import React from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  IconButton,
  Paper,
  TextField,
  Rating,
  Dialog,
  DialogContent,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/common/Header';
import MainMenuDrawer from '../../components/Main/MenuDrawer';
import {
  getShopDetailApi,
  getShopReviewsApi,
  createShopReviewApi,
  updateShopReviewApi,
  deleteShopReviewApi,
  uploadShopReviewImageApi,
  getServerBaseUrl,
} from '../../api';
import ConfirmModal from '../../components/common/ConfirmModal';

const ShopDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [shop, setShop] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState('description');
  const [showFullDescription, setShowFullDescription] = React.useState(false);
  const [photoIndex, setPhotoIndex] = React.useState(0);
  const [reviewRating, setReviewRating] = React.useState(0);
  const [reviewText, setReviewText] = React.useState('');
  const [reviewImages, setReviewImages] = React.useState([]); // 후기 이미지 URL 리스트
  const [reviewWriterName, setReviewWriterName] = React.useState(''); // 관리자가 설정할 수 있는 작성자 이름
  const [currentUser, setCurrentUser] = React.useState(null);
  const [reviews, setReviews] = React.useState([]);
  const [submittingReview, setSubmittingReview] = React.useState(false);
  const [editingReviewId, setEditingReviewId] = React.useState(null);
  const [deleteTargetReviewId, setDeleteTargetReviewId] = React.useState(null);
  const [imagePreviewImages, setImagePreviewImages] = React.useState([]);
  const [imagePreviewIndex, setImagePreviewIndex] = React.useState(0);
  const [imagePreviewOpen, setImagePreviewOpen] = React.useState(false);
  const [imagePreviewMeta, setImagePreviewMeta] = React.useState(null);
  const [imagePreviewTextExpanded, setImagePreviewTextExpanded] = React.useState(false);
  const [imagePreviewShowMeta, setImagePreviewShowMeta] = React.useState(false);
  const uploadRefs = React.useRef({
    reviewImages: [],
    cleaned: false,
    saved: false,
  });

  React.useEffect(() => {
    async function load() {
      try {
        const res = await getShopDetailApi(id);
        setShop(res.data);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }

    if (id) {
      load();
    }
  }, [id]);

  React.useEffect(() => {
    try {
      const raw = window.localStorage.getItem('currentUser');
      if (raw) {
        const user = JSON.parse(raw);
        setCurrentUser(user);
        // 관리자인 경우 기본 이름 설정 (수정 모드가 아니고 reviewWriterName이 비어있을 때만)
        if (user?.role === 'ADMIN' && user?.name && !editingReviewId && !reviewWriterName) {
          setReviewWriterName(user.name);
        }
      } else {
        setCurrentUser(null);
        if (!editingReviewId) {
          setReviewWriterName('');
        }
      }
    } catch {
      setCurrentUser(null);
      if (!editingReviewId) {
        setReviewWriterName('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingReviewId]);

  React.useEffect(() => {
    setPhotoIndex(0);
  }, [shop?.shopId]);


  React.useEffect(() => {
    async function loadReviews() {
      if (!id) return;
      try {
        const res = await getShopReviewsApi({ shopId: id, page: 1, limit: 50 });
        setReviews(res.data?.items || []);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        // no-op
      }
    }

    loadReviews();
  }, [id]);

  const serverUrl = React.useMemo(
    () => getServerBaseUrl(),
    [],
  );

  const getImageUrl = (path) => {
    if (!path) return '/image/hotel1_image.png';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    return `${serverUrl}${path}`;
  };

  const openImagePreview = (paths, startIndex = 0, meta = null) => {
    const list = Array.isArray(paths) ? paths : [paths];
    const cleaned = list
      .map((p) => String(p || '').trim())
      .filter((p) => p);
    if (cleaned.length === 0) return;
    const safeIndex = Number.isFinite(startIndex) ? Math.max(0, Math.min(cleaned.length - 1, startIndex)) : 0;
    setImagePreviewImages(cleaned);
    setImagePreviewIndex(safeIndex);
    setImagePreviewMeta(meta && typeof meta === 'object' ? meta : null);
    setImagePreviewShowMeta(Boolean(meta));
    setImagePreviewTextExpanded(false);
    setImagePreviewOpen(true);
  };

  const closeImagePreview = () => {
    setImagePreviewOpen(false);
    setImagePreviewImages([]);
    setImagePreviewIndex(0);
    setImagePreviewMeta(null);
    setImagePreviewShowMeta(false);
    setImagePreviewTextExpanded(false);
  };

  const cleanupUploads = React.useCallback(() => {
    const { reviewImages, cleaned, saved } = uploadRefs.current;
    if (cleaned || saved || reviewImages.length === 0) return;
    try {
      const url = `${getServerBaseUrl()}/api/shop-reviews/uploads/cleanup`;
      const payload = JSON.stringify({ files: reviewImages });
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
    } catch (e) {
      // ignore
    } finally {
      uploadRefs.current.cleaned = true;
    }
  }, []);

  React.useEffect(() => {
    const handleBeforeUnload = () => {
      const { reviewImages, cleaned, saved } = uploadRefs.current;
      if (cleaned || saved || reviewImages.length === 0) return;
      try {
        const url = `${getServerBaseUrl()}/api/shop-reviews/uploads/cleanup`;
        const payload = JSON.stringify({ files: reviewImages });
        navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
        uploadRefs.current.cleaned = true;
      } catch (e) {
        // ignore
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      cleanupUploads();
    };
  }, [cleanupUploads]);

  const handlePrevPreview = () => {
    setImagePreviewIndex((prev) => {
      const len = imagePreviewImages.length || 0;
      if (len <= 1) return 0;
      return (prev - 1 + len) % len;
    });
  };

  const handleNextPreview = () => {
    setImagePreviewIndex((prev) => {
      const len = imagePreviewImages.length || 0;
      if (len <= 1) return 0;
      return (prev + 1) % len;
    });
  };

  React.useEffect(() => {
    if (!imagePreviewOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeImagePreview();
        return;
      }
      if (e.key === 'ArrowLeft') handlePrevPreview();
      if (e.key === 'ArrowRight') handleNextPreview();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagePreviewOpen, imagePreviewImages.length]);

  const getDescriptionPreview = (html, maxLength = 80) => {
    if (!html) return { preview: '', isTruncated: false };
    const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    if (text.length <= maxLength) {
      return { preview: text, isTruncated: false };
    }
    return { preview: `${text.slice(0, maxLength)}...`, isTruncated: true };
  };

  const parseReviewContent = (raw) => {
    if (!raw) return { text: '', images: [] };

    if (typeof raw === 'string') {
      try {
        const data = JSON.parse(raw);
        if (data && (Object.prototype.hasOwnProperty.call(data, 'text') || data.images)) {
          return {
            text: data.text || '',
            images: Array.isArray(data.images) ? data.images.slice(0, 3) : [],
          };
        }
      } catch (e) {
      }
      return { text: raw, images: [] };
    }

    return { text: String(raw), images: [] };
  };

  const handleAddReviewImage = React.useCallback(() => {
    if (!currentUser) {
      // eslint-disable-next-line no-alert
      window.alert('로그인 후 사진을 등록할 수 있습니다.');
      return;
    }

    if (reviewImages.length >= 3) {
      // eslint-disable-next-line no-alert
      window.alert('사진은 최대 3장까지 등록할 수 있습니다.');
      return;
    }

    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const { files } = input;
      if (!files || !files[0]) return;

      const file = files[0];
      const formData = new FormData();
      formData.append('image', file);

      try {
        const res = await uploadShopReviewImageApi(formData);
        const path = res?.data?.url;
        if (!path) return;

        setReviewImages((prev) => [...prev, path]);
        uploadRefs.current.reviewImages.push(path);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        // eslint-disable-next-line no-alert
        window.alert('이미지 업로드 실패');
      }
    };
  }, [reviewImages.length, currentUser]);

  const handleSubmitReview = async () => {
    if (!id) return;
    const rating = reviewRating;
    const content = (reviewText || '').trim();

    if (!currentUser) {
      // eslint-disable-next-line no-alert
      window.alert('로그인 후 후기를 등록할 수 있습니다.');
      return;
    }

    if (!rating) {
      // eslint-disable-next-line no-alert
      window.alert('평점을 선택해주세요.');
      return;
    }

    if (!content && reviewImages.length === 0) {
      // eslint-disable-next-line no-alert
      window.alert('후기 내용 또는 사진을 등록해주세요.');
      return;
    }

    const payloadContent = JSON.stringify({
      text: content,
      images: reviewImages,
    });

    try {
      setSubmittingReview(true);

      const isAdmin = currentUser?.role === 'ADMIN';
      const writerName = isAdmin && reviewWriterName?.trim() ? reviewWriterName.trim() : null;

      if (editingReviewId) {
        await updateShopReviewApi(editingReviewId, {
          rating,
          content: payloadContent,
          photos: reviewImages,
          writerName,
        });
      } else {
        await createShopReviewApi({
          shopId: id,
          rating,
          content: payloadContent,
          photos: reviewImages,
          writerName,
        });
      }

      setReviewRating(0);
      setReviewText('');
      setReviewImages([]);
      setEditingReviewId(null);
      if (isAdmin && currentUser?.name) {
        setReviewWriterName(currentUser.name);
      } else {
        setReviewWriterName('');
      }
      uploadRefs.current.saved = true;
      uploadRefs.current.cleaned = true;
      uploadRefs.current.reviewImages = [];

      const [detailRes, reviewsRes] = await Promise.all([
        getShopDetailApi(id),
        getShopReviewsApi({ shopId: id, page: 1, limit: 50 }),
      ]);
      setShop(detailRes.data);
      setReviews(reviewsRes.data?.items || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      // eslint-disable-next-line no-alert
      window.alert(err.message || '후기 등록 중 오류가 발생했습니다.');
    } finally {
      setSubmittingReview(false);
    }
  };

  const renderTabContent = () => {
    if (!shop) return null;

    if (activeTab === 'reviews') {
      return (
        <Box sx={{ mt: 1 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid #e5e5e5',
              mb: 2,
              p: 1.5,
            }}
          >
            {!currentUser ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontSize: 13.5, color: '#6b7280' }}
                >
                  로그인 후 후기를 남겨보세요.
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {currentUser?.role === 'ADMIN' ? (
                    <TextField
                      value={reviewWriterName}
                      onChange={(e) => setReviewWriterName(e.target.value)}
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          fontSize: 13,
                          fontWeight: 600,
                          py: 0,
                        },
                      }}
                    />
                  ) : (
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600 }}
                    >
                      {currentUser.name || currentUser.loginId || '작성자'}
                    </Typography>
                  )}
                  <Rating
                    size="small"
                    value={reviewRating}
                    onChange={(_, value) => setReviewRating(value || 0)}
                  />
                </Box>

                <TextField
                  multiline
                  minRows={3}
                  fullWidth
                  placeholder="후기를 남겨보세요."
                  variant="standard"
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  InputProps={{
                    disableUnderline: true,
                  }}
                  sx={{
                    bgcolor: '#f9f9fb',
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                    fontSize: 13,
                  }}
                />

                <Box sx={{ mt: 1.2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleAddReviewImage}
                    sx={{
                      justifyContent: 'center',
                      borderRadius: 2,
                      borderColor: '#e5e5e5',
                      bgcolor: '#ffffff',
                      textTransform: 'none',
                      fontSize: 13,
                      py: 1,
                      '&:hover': {
                        bgcolor: '#f9fafb',
                        borderColor: '#d4d4d8',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          border: '1.5px solid #4b5563',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <AddIcon sx={{ fontSize: 14, color: '#4b5563' }} />
                      </Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 500, lineHeight: 1, color: '#4b5563' }}>
                        사진 추가 ({reviewImages.length}/3)
                      </Typography>
                    </Box>
                  </Button>

                  {reviewImages.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.8,
                        flexWrap: 'nowrap',
                        overflowX: 'auto',
                        width: '100%',
                        mt: 0.8,
                        pb: 0.5,
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(0,0,0,0.25) transparent',
                        '&::-webkit-scrollbar': { height: 6 },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'rgba(0,0,0,0.2)',
                          borderRadius: 999,
                        },
                        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                      }}
                      onWheel={(e) => {
                        // PC에서 세로 스크롤 휠로도 가로 이미지 목록을 볼 수 있게 처리
                        if (e.deltaY) {
                          e.currentTarget.scrollLeft += e.deltaY;
                        }
                      }}
                    >
                      {reviewImages.map((path, idx) => (
                        <Box
                          key={path}
                          sx={{
                            flex: '0 0 auto',
                            width: 56,
                            height: 56,
                            borderRadius: 1,
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <Box
                            component="img"
                            src={getImageUrl(path)}
                            alt="후기 이미지"
                            onClick={() => openImagePreview(reviewImages, idx)}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              cursor: 'pointer',
                            }}
                          />
                          <Button
                            size="small"
                            onClick={() => {
                              setReviewImages((prev) => prev.filter((p) => p !== path));
                            }}
                            sx={{
                              position: 'absolute',
                              top: -4,
                              right: -4,
                              minWidth: 0,
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              bgcolor: 'rgba(0,0,0,0.6)',
                              color: '#fff',
                              fontSize: 10,
                              p: 0,
                            }}
                          >
                            ×
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: 1,
                    gap: 1,
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => {
                      setReviewRating(0);
                      setReviewText('');
                      setReviewImages([]);
                      setEditingReviewId(null);
                      if (currentUser?.role === 'ADMIN' && currentUser?.name) {
                        setReviewWriterName(currentUser.name);
                      } else {
                        setReviewWriterName('');
                      }
                    }}
                    sx={{ fontSize: 13, color: '#6b7280' }}
                  >
                    취소
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      fontSize: 13,
                      bgcolor: '#111827',
                      borderRadius: 999,
                      px: 2.5,
                    }}
                    onClick={handleSubmitReview}
                    disabled={submittingReview}
                  >
                    {editingReviewId ? '수정' : '등록'}
                  </Button>
                </Box>
              </>
            )}
          </Paper>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
            {reviews.length === 0 && (
              <Typography variant="body2" sx={{ fontSize: 13.5, color: '#6b7280' }}>
                아직 등록된 후기가 없습니다.
              </Typography>
            )}

            {reviews.map((review) => {
              const currentUserId = currentUser
                ? currentUser.id || currentUser.user_id || currentUser.userId
                : null;

              const isAdmin = currentUser?.role === 'ADMIN';
              const isOwner = currentUserId
                && String(currentUserId) === String(review.writerId);

              const { text: reviewBody, images: reviewImagePaths } = parseReviewContent(
                review.content,
              );

              return (
              <Paper
                key={review.reviewId}
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: '1px solid #e5e5e5',
                  p: 1.5,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.3 }}
                >
                  {review.writerName || '작성자'}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.3,
                    mb: 0.5,
                  }}
                >
                  <Rating
                    value={review.rating}
                    size="small"
                    readOnly
                    sx={{ '& .MuiRating-iconFilled': { color: '#ffb400' } }}
                  />
                </Box>
                {reviewBody && (
                  <Typography
                    variant="body2"
                    sx={{ fontSize: 13, mb: reviewImagePaths.length > 0 ? 0.5 : 0 }}
                  >
                    {reviewBody}
                  </Typography>
                )}

                {reviewImagePaths.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.8,
                      flexWrap: 'nowrap',
                      overflowX: 'auto',
                      width: '100%',
                      mb: 0.5,
                      pb: 0.5,
                      WebkitOverflowScrolling: 'touch',
                      scrollbarWidth: 'thin',
                      scrollbarColor: 'rgba(0,0,0,0.25) transparent',
                      '&::-webkit-scrollbar': { height: 6 },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        borderRadius: 999,
                      },
                      '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                    }}
                    onWheel={(e) => {
                      if (e.deltaY) {
                        e.currentTarget.scrollLeft += e.deltaY;
                      }
                    }}
                  >
                    {reviewImagePaths.map((path, idx) => (
                      <Box
                        key={path}
                        component="img"
                        src={getImageUrl(path)}
                        alt="후기 이미지"
                        onClick={() =>
                          openImagePreview(reviewImagePaths, idx, {
                            rating: review.rating,
                            writerRole: review.writerRole,
                            writerName: review.writerName,
                            createdAt: review.createdAt,
                            text: reviewBody,
                          })
                        }
                        sx={{
                          flex: '0 0 auto',
                          width: 72,
                          height: 72,
                          borderRadius: 1,
                          objectFit: 'cover',
                          cursor: 'pointer',
                        }}
                      />
                    ))}
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontSize: 11, color: '#6b7280' }}
                  >
                    {review.createdAt
                      ? new Date(review.createdAt).toLocaleDateString('ko-KR')
                      : ''}
                  </Typography>

                  {(isOwner || isAdmin) && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {isOwner && (
                        <Button
                          size="small"
                          variant="text"
                          sx={{ fontSize: 11, minWidth: 0, p: 0 }}
                          onClick={() => {
                            setEditingReviewId(review.reviewId);
                            setReviewRating(review.rating);
                            setReviewText(reviewBody);
                            setReviewImages(reviewImagePaths);
                            // 관리자인 경우 작성자 이름 설정
                            // display_name이 있으면 그것을 사용, 없으면 user_name(관리자가 처음 설정한 이름) 사용
                            if (currentUser?.role === 'ADMIN') {
                              const initialDisplayName = review.display_name !== null && review.display_name !== undefined
                                ? review.display_name
                                : (review.user_name || currentUser?.name || '');
                              setReviewWriterName(initialDisplayName);
                            }
                          }}
                        >
                          수정
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="text"
                        sx={{ fontSize: 11, minWidth: 0, p: 0, color: '#dc2626' }}
                        onClick={() => {
                          setDeleteTargetReviewId(review.reviewId);
                        }}
                      >
                        삭제
                      </Button>
                    </Box>
                  )}
                </Box>
              </Paper>
              );
            })}
          </Box>
        </Box>
      );
    }

    if (activeTab === 'description') {
  return (
        <Box sx={{ fontSize: 13.5, lineHeight: 1.6 }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 0.3, fontSize: 15 }}
            >
              위치
            </Typography>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-line' }}
            >
              {shop.locationText || '등록된 위치 정보가 없습니다.'}
          </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 0.3, fontSize: 15 }}
            >
              영업시간
            </Typography>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-line' }}
            >
              {shop.openingHours || '등록된 영업시간 정보가 없습니다.'}
            </Typography>
          </Box>

          {shop.description ? (
            <>
              <Box sx={{ mb: 0.8 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.3, fontSize: 15 }}
                >
                  소개
                </Typography>
              </Box>
              {(() => {
                const { isTruncated } = getDescriptionPreview(
                  shop.description,
                  120,
                );

                const baseDescriptionStyles = {
                  mt: 0.5,
                  fontSize: 13.5,
                  lineHeight: 1.7,
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    fontSize: 15,
                    fontWeight: 600,
                    margin: '10px 0 4px',
                  },
                  '& p, & li': {
                    fontSize: 13.5,
                    lineHeight: 1.7,
                    margin: '2px 0',
                  },
                  '& ul, & ol': {
                    paddingLeft: '18px',
                    margin: '4px 0',
                  },
                };

                if (!isTruncated || showFullDescription) {
                  return (
                    <Box
                      sx={baseDescriptionStyles}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: shop.description }}
                    />
                  );
                }

                return (
                  <Box sx={{ mt: 0.5 }}>
                    <Box
                      sx={{
                        ...baseDescriptionStyles,
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: shop.description }}
                    />
                    <Box sx={{ mt: 0.5 }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setShowFullDescription(true)}
                        sx={{
                          p: 0,
                          minWidth: 0,
                          fontSize: 12,
                          textDecoration: 'underline',
                        }}
                      >
                        더보기
                      </Button>
                    </Box>
                  </Box>
                );
              })()}
            </>
          ) : (
            <Typography
              variant="body2"
              sx={{ fontSize: 13.5, lineHeight: 1.6 }}
            >
              등록된 상세 설명이 없습니다.
            </Typography>
          )}
        </Box>
      );
    }

    return null;
  };

  const renderStars = (avgRating) => {
    const rating = Number(avgRating) || 0;
    const full = Math.floor(rating);
    const hasHalf = rating - full >= 0.5;

    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.1 }}>
        {Array.from({ length: 5 }).map((_, index) => {
          const i = index + 1;
          if (rating <= 0) {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <span key={i} style={{ color: '#d0d0d0', fontSize: 12 }}>★</span>
            );
          }
          if (i <= full) {
            return (
              <span key={i} style={{ color: '#ffb400', fontSize: 12 }}>★</span>
            );
          }
          if (hasHalf && i === full + 1) {
            return (
              <span key={i} style={{ color: '#ffb400', fontSize: 12 }}>☆</span>
            );
          }
          return (
            <span key={i} style={{ color: '#d0d0d0', fontSize: 12 }}>★</span>
          );
        })}
      </Box>
    );
  };

  return (
    <Box>
      <Box
        sx={{
          maxWidth: 430,
          mx: 'auto',
          bgcolor: 'white',
          minHeight: '100vh',
          borderRadius: 0,
          boxShadow: '0 0 20px rgba(0,0,0,0.06)',
          px: 2,
          position: 'relative',
        }}
      >
        <MainMenuDrawer
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
        />

        <Header variant="full" onClickMenu={() => setMenuOpen(true)} />

        <Box
          sx={{
            mb: 2,
            mt: 1,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            width: 'fit-content',
          }}
          onClick={() => {
            const fromList = location.state?.from;
            if (fromList) {
              navigate(fromList);
              return;
            }
            navigate('/shops');
          }}
        >
          <ArrowBackIosIcon sx={{ fontSize: 18, mr: 0.5 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 16 }}>
            유흥/골프/식당
          </Typography>
        </Box>

        {shop && (
          <>
            <Box sx={{ mt: 1.5, mb: 1.5 }}>
              <Typography
                variant="h6"
                sx={{ fontSize: 20, fontWeight: 700, mb: 0.4 }}
              >
                {shop.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontSize: 13, color: '#111827' }}
              >
                {renderStars(shop.avgRating)}
                <span style={{ marginLeft: 4, fontSize: 12 }}>
                  ({shop.reviewCount ?? 0})
                </span>
              </Typography>
            </Box>

            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              aria-label="shop-detail-tabs"
              variant="fullWidth"
              sx={{
                minHeight: 36,
                '& .MuiTab-root': {
                  minHeight: 36,
                  fontSize: 13,
                  px: 1.5,
                  flex: 1,
                  maxWidth: 'none',
                },
                '& .MuiTabs-flexContainer': {
                  display: 'flex',
                },
                '& .MuiTabs-indicator': {
                  height: 2,
                },
              }}
            >
              <Tab value="description" label="상세설명" />
              <Tab value="reviews" label="후기" />
            </Tabs>

            {activeTab !== 'reviews' && (
              <Box
                sx={{
                  mt: 1.5,
                  mb: 1.5,
                  borderRadius: 1,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {(() => {
                  const photos = Array.isArray(shop.photos) && shop.photos.length > 0
                    ? shop.photos
                    : (shop.thumbnailUrl ? [{ fileUrl: shop.thumbnailUrl }] : []);
                  const previewPaths = photos
                    .map((p) => p && (p.fileUrl || p.thumbnailUrl))
                    .filter((p) => p);
                  const handleOpenPreview = (idx = 0) => {
                    if (previewPaths.length === 0) return;
                    openImagePreview(previewPaths, idx);
                  };

                  if (photos.length === 0) {
                    return (
                      <Box
                        component="img"
                        src="/image/hotel1_image.png"
                        alt={shop.name}
                        sx={{
                          width: 380,
                          height: 205,
                          maxWidth: '100%',
                          objectFit: 'cover',
                          display: 'block',
                          mx: 'auto',
                          cursor: previewPaths.length > 0 ? 'zoom-in' : 'default',
                        }}
                        onClick={() => handleOpenPreview(0)}
                      />
                    );
                  }

                  if (photos.length === 1) {
                    return (
                      <Box
                        component="img"
                        src={getImageUrl(photos[0].fileUrl || shop.thumbnailUrl)}
                        alt={shop.name}
                        sx={{
                          width: 380,
                          height: 205,
                          maxWidth: '100%',
                          objectFit: 'cover',
                          display: 'block',
                          mx: 'auto',
                          cursor: previewPaths.length > 0 ? 'zoom-in' : 'default',
                        }}
                        onClick={() => handleOpenPreview(0)}
                      />
                    );
                  }

                  const safeIndex = Math.min(photoIndex, photos.length - 1);
                  const current = photos[safeIndex];

                  const handlePrev = () => {
                    if (safeIndex <= 0) return;
                    setPhotoIndex((prev) => Math.max(prev - 1, 0));
                  };

                  const handleNext = () => {
                    if (safeIndex >= photos.length - 1) return;
                    setPhotoIndex((prev) => Math.min(prev + 1, photos.length - 1));
                  };

                  return (
                    <Box
                      sx={{
                        position: 'relative',
                        width: 380,
                        height: 205,
                        maxWidth: '100%',
                        mx: 'auto',
                      }}
                    >
                      <Box
                        component="img"
                        src={getImageUrl(current.fileUrl)}
                        alt={`${shop.name} 사진 ${safeIndex + 1}`}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: 1,
                          display: 'block',
                          cursor: previewPaths.length > 0 ? 'zoom-in' : 'default',
                        }}
                        onClick={() => handleOpenPreview(safeIndex)}
                      />

                      {safeIndex > 0 && (
                        <IconButton
                          size="small"
                          onClick={handlePrev}
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: 4,
                            transform: 'translateY(-50%)',
                            bgcolor: 'rgba(0,0,0,0.25)',
                            color: '#fff',
                            '&:hover': {
                              bgcolor: 'rgba(0,0,0,0.4)',
                            },
                          }}
                        >
                          <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                      )}

                      {safeIndex < photos.length - 1 && (
                        <IconButton
                          size="small"
                          onClick={handleNext}
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            right: 4,
                            transform: 'translateY(-50%)',
                            bgcolor: 'rgba(0,0,0,0.25)',
                            color: '#fff',
                            '&:hover': {
                              bgcolor: 'rgba(0,0,0,0.4)',
                            },
                          }}
                        >
                          <ChevronRightIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  );
                })()}
              </Box>
            )}

            <Box sx={{ mb: 4 }}>
              {renderTabContent()}
            </Box>
          </>
        )}

        <ConfirmModal
          open={Boolean(deleteTargetReviewId)}
          message="해당 후기를 삭제하시겠습니까?"
          confirmText="삭제"
          cancelText="취소"
          onCancel={() => setDeleteTargetReviewId(null)}
          onConfirm={async () => {
            if (!deleteTargetReviewId) return;
            try {
              await deleteShopReviewApi(deleteTargetReviewId);
              setDeleteTargetReviewId(null);
              const [detailRes, reviewsRes] = await Promise.all([
                getShopDetailApi(id),
                getShopReviewsApi({ shopId: id, page: 1, limit: 50 }),
              ]);
              setShop(detailRes.data);
              setReviews(reviewsRes.data?.items || []);
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error(err);
              // eslint-disable-next-line no-alert
              window.alert(err.message || '후기 삭제 중 오류가 발생했습니다.');
            }
          }}
        />

        <Dialog
          open={imagePreviewOpen}
          onClose={closeImagePreview}
          fullWidth
          maxWidth="sm"
          slotProps={{
            backdrop: {
              sx: {
                backgroundColor: 'rgba(0, 0, 0, 0.35)',
              },
            },
          }}
          PaperProps={{
            sx: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              m: { xs: 1, sm: 2 },
              maxWidth: 430,
              width: '100%',
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <Box
              sx={{
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'transparent',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: 340, sm: 480 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#000',
                  overflow: 'hidden',
                }}
              >
                <IconButton
                  onClick={closeImagePreview}
                  aria-label="close"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    color: '#111',
                    bgcolor: 'rgba(255,255,255,0.9)',
                    width: 30,
                    height: 30,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>

                {imagePreviewImages.length > 1 && (
                  <IconButton
                    onClick={handlePrevPreview}
                    sx={{
                      position: 'absolute',
                      left: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(0,0,0,0.25)',
                      color: '#fff',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.35)' },
                    }}
                    aria-label="prev"
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                )}

                <Box
                  component="img"
                  src={imagePreviewImages[imagePreviewIndex] ? getImageUrl(imagePreviewImages[imagePreviewIndex]) : ''}
                  alt="후기 이미지"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />

                {imagePreviewImages.length > 1 && (
                  <IconButton
                    onClick={handleNextPreview}
                    sx={{
                      position: 'absolute',
                      right: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(0,0,0,0.25)',
                      color: '#fff',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.35)' },
                    }}
                    aria-label="next"
                  >
                    <ChevronRightIcon />
                  </IconButton>
                )}

                <Box
                  sx={{
                    position: 'absolute',
                    right: 12,
                    bottom: 12,
                    px: 1,
                    py: 0.5,
                    borderRadius: 999,
                    bgcolor: 'rgba(0,0,0,0.45)',
                    color: '#fff',
                    fontSize: 12,
                  }}
                >
                  {imagePreviewImages.length > 0 ? `${imagePreviewIndex + 1}/${imagePreviewImages.length}` : ''}
                </Box>
              </Box>

              {imagePreviewShowMeta && (
                <Box
                  sx={{
                    bgcolor: '#fff',
                    px: 2,
                    py: 1.5,
                    boxShadow: '0 -6px 18px rgba(0,0,0,0.12)',
                    overflowY: 'auto',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Rating value={Number(imagePreviewMeta?.rating || 0)} readOnly size="small" />
                    <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
                      {imagePreviewMeta?.createdAt ? new Date(imagePreviewMeta.createdAt).toLocaleDateString('ko-KR') : ''}
                    </Typography>
                  </Box>

              <Typography sx={{ fontSize: 12.5, color: '#111827', mt: 0.75, fontWeight: 600 }}>
                {String(imagePreviewMeta?.writerName || '').trim() || '사용자'}
              </Typography>

                  {String(imagePreviewMeta?.text || '').trim() && (
                    <Box sx={{ mt: 0.75 }}>
                      {(() => {
                        const full = String(imagePreviewMeta?.text || '');
                        const limit = 90;
                        const needMore = full.length > limit;
                        const shown = imagePreviewTextExpanded || !needMore ? full : `${full.slice(0, limit)}...`;
                        return (
                          <>
                            <Typography sx={{ fontSize: 12.5, color: '#374151', whiteSpace: 'pre-line' }}>
                              {shown}
                            </Typography>
                            {needMore && (
                              <Typography
                                sx={{ fontSize: 12, color: '#6b7280', mt: 0.5, textAlign: 'right', cursor: 'pointer' }}
                                onClick={() => setImagePreviewTextExpanded((v) => !v)}
                              >
                                {imagePreviewTextExpanded ? '접기' : '더보기'}
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );
};

export default ShopDetail;

